import { Injectable } from '@angular/core';
import { toImmutableMap, UuidService } from '@datachain/ui-sdk/common';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import {
	AppTagItemEntity,
	ColumnType,
} from '@datachain/ui-sdk/components/dc-types';
import { Map, OrderedMap, OrderedSet, Set } from 'immutable';

import dayjs from '../core/configs/dayjs';
import { DxListsStorage } from '../core/dx-lists.storage';
import { DcDxGrids, DxColDataType } from '../core/dx-types';
import { ProjectStorage } from '../core/project.storage';
import { DcRouteUrlBuilder } from '../core/utils/dc-route-url-builder';
import { normalize } from '../core/utils/normalize';
import {
	IConsumerToMap,
	IMarketplaceGroup,
	IMarketplaceMember,
	IMarketPlaceMembers,
	IMarketplaceUser,
	IMembersMapping,
} from '../requesters/core/models/marketplace-members.model';
import { IDcMetadataResponse } from '../requesters/core/models/metadata.model';
import { IExpositionPublicationAccessConfig } from '../requesters/exposition/models/draft-publication.model';
import { IExpositionColumnConfig } from '../requesters/exposition/models/exposition-column-config.model';
import { IExpositionResponse } from '../requesters/exposition/models/exposition.model';
import {
	IConsumerForDatablockExposition,
	IEligibleForMigration,
	IMigratedExposition,
	IPreviousExpositionConsumer,
} from '../requesters/exposition/models/migrated-exposition.model';
import { IPublicMetadata } from '../requesters/exposition/models/public-metadata.model';
import {
	ActionType,
	HistoryStatus,
	IPublicationHistory,
	IPublicationHistoryDetails,
} from '../requesters/exposition/models/publication-history.model';
import { IShareableDatablockColumn } from '../requesters/exposition/models/shareable-datablock-column.model';
import { IShareableDatablock } from '../requesters/exposition/models/shareable-datablock.model';
import {
	ExpositionsAvailableSimulationColumn,
	ExpositionSimulationAccess,
	ExpositionSimulationAccessEnum,
	ExpositionSimulationGroup,
	ExpositionSimulationUser,
	ParsedSimulationDataError,
	ParsedSimulationExec,
	SimulatedExpositionUris,
	SimulationExpositionMetadata,
} from '../requesters/exposition/models/simulator.models';
import { ConsumerMetadataItemEntity } from './components/exposition-consumers-mapping/consumer-mapping-popup/consumer-metadata-item.entity';
import { ExpositionEndpointMetadataEntity } from './components/exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
import {
	ExpositionHistoryItemEntity,
	HistoryActionType,
} from './components/exposition-history-list/exposition-history-item.entity';
import { ExpositionHistoryListEntity } from './components/exposition-history-list/exposition-history-list.entity';
import { ExpositionMetadataEntity } from './components/exposition-internal-metadata-config/exposition-metadata.entity';
import { ExpositionItemEntity } from './components/exposition-list/exposition-item.entity';
import {
	ExpositionListEntity,
	IExpositionListExtra,
} from './components/exposition-list/exposition-list.entity';
import { MigrationReadyExpositionItemEntity } from './components/exposition-migrations-list/exposition-migration-stepper/migration-ready-exposition-item.entity';
import { MigrationReadyExpositionList } from './components/exposition-migrations-list/exposition-migration-stepper/migration-ready-exposition-list.entity';
import { MigratedExpositionItemEntity } from './components/exposition-migrations-list/migrated-exposition-item.entity';
import { MigratedExpositionListEntity } from './components/exposition-migrations-list/migrated-exposition-list.entity';
import {
	SimulationAccessEntity,
	SimulationAccessType,
} from './components/exposition-simulator/simulation-access.entity';
import { SimulationColumnsItemEntity } from './components/exposition-simulator/simulation-columns-item.entity';
import { SimulationColumnsListEntity } from './components/exposition-simulator/simulation-columns-list.entity';
import { SimulationExpositionMetadataEntity } from './components/exposition-simulator/simulation-exposition-metadata.entity';
import { SimulationGroupEntity } from './components/exposition-simulator/simulation-group.entity';
import { SimulationResultsEntity } from './components/exposition-simulator/simulation-results.entity';
import { SimulationUrisEntity } from './components/exposition-simulator/simulation-uris.entity';
import { SimulationUserEntity } from './components/exposition-simulator/simulation-user.entity';
import { ExpositionDatablockColumnEntity } from './components/exposition-stepper/exposition-datablock-list/exposition-datablock-column.entity';
import { ExpositionDatablockListEntity } from './components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.entity';
import { ExpositionDatablockEntity } from './components/exposition-stepper/exposition-datablock-list/exposition-datablock.entity';
import { ExpositionAccessListEntity } from './components/exposition-view/exposition-access-list/exposition-access-list.entity';
import {
	AccessType,
	ExpositionAccessEntity,
} from './components/exposition-view/exposition-access-list/exposition-access.entity';
import { ExpositionEntity } from './components/exposition-view/exposition.entity';
import { ExpositionHistoryEntity } from './components/exposition-view/published-exposition/exposition-history.entity';
import { PublishedExpositionEntity } from './components/exposition-view/published-exposition/published-exposition.entity';
import { AccessConfigEntity } from './domain/exposition-access-config.entity';
import { ExpositionColumnConfigListEntity } from './domain/exposition-column-config-list.entity';
import { ExpositionColumnConfigEntity } from './domain/exposition-column-config.entity';
import { ExpositionConsumerListEntity } from './domain/exposition-consumer-list.entity';
import { ExpositionConsumerMappingEntity } from './domain/exposition-consumer-mapping.entity';
import { ExpositionConsumerEntity } from './domain/exposition-consumer.entity';
import { PublicationStatus } from './domain/publication-status';

@Injectable()
export class ExpositionsAdapter {
	private readonly columnTypeToDxColumnType: Record<
		string | ColumnType,
		DxColDataType
	> = {
		[ColumnType.Str]: 'string',
		[ColumnType.File]: 'string',
		[ColumnType.Dec]: 'number',
		[ColumnType.Double]: 'number',
		[ColumnType.Date]: 'datetime',
		[ColumnType.BigInt]: 'number',
		[ColumnType.Bin]: 'string',
		[ColumnType.Words]: 'string',
		[ColumnType.Int]: 'number',
		[ColumnType.Bool]: 'boolean',
		[ColumnType.Geo]: 'string',
		[ColumnType.Unknown]: 'string',
	};
	private readonly simulationAccessTypeAdapter = {
		[ExpositionSimulationAccessEnum.Open]: SimulationAccessType.Open,
		[ExpositionSimulationAccessEnum.Limited]: SimulationAccessType.Restricted,
	};

	private readonly PublicationStatusMapping: Record<
		string | HistoryStatus,
		PublicationStatus
	> = {
		[HistoryStatus.UNPUBLISHED]: PublicationStatus.Unpublished,
		[HistoryStatus.AVAILABLE]: PublicationStatus.Available,
		[HistoryStatus.UNAVAILABLE]: PublicationStatus.Unavailable,
		[HistoryStatus.WAITING]: PublicationStatus.Waiting,
		[HistoryStatus.RUNNING]: PublicationStatus.Running,
		[HistoryStatus.SUCCESS]: PublicationStatus.Success,
		[HistoryStatus.ERROR]: PublicationStatus.Error,
		[HistoryStatus.UNKNOWN]: PublicationStatus.Unknown,
	};

	public constructor(
		private readonly projectStorage: ProjectStorage,
		private readonly uuidService: UuidService,
		private readonly dxListsStorage: DxListsStorage
	) {}

	public generateExposition(response: IExpositionResponse): ExpositionEntity {
		const accessList = this.generateAccessList();
		const accessTuples = response.draft_publication?.access?.reduce(
			(acc, curr) => {
				const entity = this.generateAccessEntity(curr);
				acc.push([`${entity.id}`, entity]);
				return acc;
			},
			new Array<[string, ExpositionAccessEntity]>()
		);
		return ExpositionEntity.build({
			id: response.id,
			columns: this.generateExpositionColumns(
				response.draft_publication?.columns ?? []
			),
			accessPoints: accessList.setElements(Map(accessTuples)),
			metadata: response.metadata
				? this.buildExpositionMetadataEntity(response.metadata)
				: ExpositionMetadataEntity.build(),
			endpointMetadata: ExpositionEndpointMetadataEntity.build({
				accessPoint: response.draft_publication?.access_point ?? '',
				details: response.draft_publication?.details ?? '',
				title: response.draft_publication?.title ?? '',
				synthesisType: response?.draft_publication?.is_synthesis_additive
					? 'Additive'
					: 'Restrictive' ?? 'Restrictive',
				keywords: OrderedSet(
					response.draft_publication?.keywords?.map((keyword) =>
						AppTagItemEntity.buildKeyword({
							label: keyword,
						})
					) ?? []
				),
			}),
			published: PublishedExpositionEntity.build({
				id: response.id,
				status: response.status
					? this.getPublicationStatus(response.status, response.history)
					: PublicationStatus.Unknown,
				datablockId: response.datablock?.id ?? -1,
				datablockLabel: response.datablock?.label ?? '',
				datablockUrl: response.datablock?.id
					? this.parseDatablockPageUrl(response.datablock.id)
					: '',
				url: response.url,
				dataUrl: response.url
					? `${response.data_root_url ?? ''}${response.url}`
					: '',
				oDataUrl: response.url
					? `${response.odata_root_url ?? ''}${response.url}`
					: '',
				publicationDate: response.publication_date
					? new Date(response.publication_date).toString()
					: '',
				history: response.history?.map((h) =>
					ExpositionHistoryEntity.build({
						duration: h.duration ? +Number(h.duration / 1000).toFixed(2) : 0,
						avatar: h.updated_by
							? AvatarEntity.build(
									`${h.updated_by.first_name} ${h.updated_by.last_name}`
							  )
							: AvatarEntity.build('user'),
						access: h.access,
						linesCount: h.nbr_of_lines,
						date: h.date ? new Date(h.date).toString() : '',
						status: h.status
							? this.getPublicationStatus(h.status)
							: PublicationStatus.Unknown,
						updateDescription: h.action
							? this.getExpositionUpdateDescription(h.action)
							: '',
					})
				),
				details: response.details,
				title: response.title,
				keywords:
					response.keywords?.map((keyword) =>
						AppTagItemEntity.buildKeyword({
							label: keyword,
						})
					) ?? [],
				dataCount: response.count,
				accessCount: response.access,
				publisherAvatar: response.draft_publication && response.draft_publication.drafted_by
					? AvatarEntity.build(
							`${response.draft_publication?.drafted_by?.first_name} ${response.draft_publication?.drafted_by?.last_name}`,
							2
					  )
					: null,
				columnsCount: response.nbr_of_cols ?? 0,
				hasActiveAccess:
					response.draft_publication?.access?.filter((a) => a.active).length !==
					0,
				hasAdditiveSynthesis: response.is_synthesis_additive ?? false,
			}),
		});
	}

	public generateExpositionColumns(
		response: Array<IExpositionColumnConfig>
	): ExpositionColumnConfigListEntity {
		const columns = this.generateColumnConfigList();
		const columnsTuples = response.reduce((acc, curr) => {
			const entity = this.generateColumnEntity(curr);
			acc.push([entity.id, entity]);
			return acc;
		}, new Array<[string, ExpositionColumnConfigEntity]>());
		return columns.setElements(Map(columnsTuples)).setHashedAndHiddenColumns(
			response.map((response) => response.uuid ?? ''),
			[]
		);
	}

	public generateExpositionAccessEntity(
		response: IExpositionPublicationAccessConfig
	): AccessConfigEntity {
		return AccessConfigEntity.build({
			id: response.id,
			label: response.label,
			description: response.description,
			type: response.type === 'LIMITED' ? AccessType.Limited : AccessType.Open,
			isActive: response.active,
			consumers: response.members
				? this.generateConsumerList(response.members)
				: ExpositionConsumerListEntity.build([]),
			filter: response.filter,
			columnsConfig: response.columns
				? this.generateExpositionColumns(response.columns)
				: ExpositionColumnConfigListEntity.build([]),
		});
	}

	public generateExistingAccessEntity(
		response: IExpositionPublicationAccessConfig,
		columns: Array<IExpositionColumnConfig>
	): AccessConfigEntity {
		const expositionColumns = this.generateExpositionColumns(columns);
		const hashedColumns = expositionColumns.elements
			.filter((c) => c.isActive && c.isHashed)
			.toArray()
			.map(([id]) => id);
		const hiddenColumns = expositionColumns.elements
			.filter((c) => c.isActive && c.isHidden)
			.toArray()
			.map(([id]) => id);

		const cols = expositionColumns.elements
			.filter((c) => c.isActive)
			.toArray()
			.map(([, c]) => c.updateHash(false).updateIsActive(false));
		if (response.columns) {
			response.columns
				.filter((c) => c.active)
				.map((column) => {
					const col = cols.find((c) => c.id === column.uuid);
					if (col) {
						cols.push(
							col
								.updateIsActive(column.active ?? false)
								.updateHash(column.hash ?? false)
						);
					}
				});
		}

		return AccessConfigEntity.build({
			id: response.id,
			label: response.label,
			description: response.description,
			type: response.type === 'LIMITED' ? AccessType.Limited : AccessType.Open,
			isActive: response.active,
			consumers: response.members
				? this.generateConsumerList(response.members)
				: ExpositionConsumerListEntity.build([]),
			filter: response.filter,
			columnsConfig: expositionColumns
				.updateElements(cols)
				.setHashedAndHiddenColumns(hashedColumns, hiddenColumns),
		});
	}

	public generateNewAccessEntity(
		columns: Array<IExpositionColumnConfig>
	): AccessConfigEntity {
		const expositionColumns = this.generateExpositionColumns(columns);

		const hashedColumns = expositionColumns.elements
			.filter((c) => c.isActive && c.isHashed)
			.toArray()
			.map(([id]) => id);
		const hiddenColumns = expositionColumns.elements
			.filter((c) => c.isActive && c.isHidden)
			.toArray()
			.map(([id]) => id);

		return AccessConfigEntity.build({
			columnsConfig: expositionColumns
				.updateElements(
					expositionColumns.elements
						.filter((c) => c.isActive)
						.toArray()
						.map(([, c]) => c.updateHash(false).updateIsActive(false))
				)
				.setHashedAndHiddenColumns(hashedColumns, hiddenColumns),
		});
	}

	public generateConsumerList(
		members: IMarketPlaceMembers
	): ExpositionConsumerListEntity {
		const list = this.generateExpositionConsumerList();
		const consumers = this.generateConsumers(members);
		return list.setElements(toImmutableMap(consumers, 'id'));
	}

	public buildExpositionMetadataEntity(
		response: IDcMetadataResponse
	): ExpositionMetadataEntity {
		const tags =
			response.tags?.map((t) =>
				AppTagItemEntity.build({
					id: t.id,
					label: t.code,
					color: t.color,
				})
			) ?? [];

		return ExpositionMetadataEntity.build({
			label: response.label,
			description: response.description,
			tags: OrderedSet(tags),
		});
	}

	public buildExpositionEndpointMetadata(
		response: IPublicMetadata
	): ExpositionEndpointMetadataEntity {
		const keywords =
			response.keywords?.map((keyword) =>
				AppTagItemEntity.buildKeyword({
					label: keyword,
				})
			) ?? [];

		return ExpositionEndpointMetadataEntity.build({
			accessPoint: response.access_point,
			details: response.details,
			title: response.title,
			keywords: OrderedSet(keywords),
			synthesisType: response.is_synthesis_additive
				? 'Additive'
				: 'Restrictive',
		});
	}

	public buildDatablockList(
		response: ReadonlyArray<IShareableDatablock>
	): ExpositionDatablockListEntity {
		const list = this.generateExpositionDatablockList();
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateExpositionDatablockEntity(curr);
			acc.push([`${curr.id}`, entity]);
			return acc;
		}, new Array<[string, ExpositionDatablockEntity]>());
		return list.setElements(Map(tuples));
	}

	public generateDatablockColumns(
		response: ReadonlyArray<IShareableDatablockColumn>
	): Array<ExpositionDatablockColumnEntity> {
		return response.map((datablock) =>
			this.generateDatablockColumnEntity(datablock)
		);
	}

	public buildColumnConfigList(
		response: ReadonlyArray<IShareableDatablockColumn>
	): ExpositionColumnConfigListEntity {
		const list = this.generateColumnConfigList();
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateColumnConfigEntity(curr);
			acc.push([entity.id, entity]);
			return acc;
		}, new Array<[string, ExpositionColumnConfigEntity]>());
		return list.setElements(Map(tuples)).setHashedAndHiddenColumns(
			response.map((response) => response.uuid ?? ''),
			[]
		);
	}

	public generateConsumers(
		response: IMarketPlaceMembers
	): Array<ExpositionConsumerEntity> {
		const consumers = new Array<ExpositionConsumerEntity>();
		response.users.forEach((user, index) => {
			const entity = ExpositionConsumerEntity.build({
				id: user.id,
				name: user?.name ?? `user-${index}`,
				isGroup: false,
				firstName: user.firstname,
				lastName: user.lastname,
				mail: user.mail,
			});
			consumers.push(entity);
		});

		response.groups.forEach((group, index) => {
			const entity = ExpositionConsumerEntity.build({
				id: group.id,
				name: group?.name ?? `group-${index}`,
				isGroup: true,
				description: group.description,
			});
			consumers.push(entity);
		});
		return consumers;
	}

	public generateUserConsumers(
		response: ReadonlyArray<IMarketplaceUser>
	): Map<string, ConsumerMetadataItemEntity> {
		const items = response.reduce((acc, item) => {
			const entity = ConsumerMetadataItemEntity.buildUserMetadata({
				id: item.id,
				login: item.login,
				firstName: item.firstname,
				lastName: item.lastname,
				mail: item.mail,
				creationDate: item.created_at ? dayjs(item.created_at) : null,
				updateDate: item.updated_at ? dayjs(item.updated_at) : null,
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, ConsumerMetadataItemEntity]>());

		return OrderedMap(items);
	}

	public generateGroupConsumers(
		response: ReadonlyArray<IMarketplaceGroup>
	): Map<string, ConsumerMetadataItemEntity> {
		const items = response.reduce((acc, item) => {
			const entity = ConsumerMetadataItemEntity.buildGroupMetadata({
				id: item.id,
				name: item.name,
				description: item.description,
				creationDate: item.created_at ? dayjs(item.created_at) : null,
				updateDate: item.updated_at ? dayjs(item.updated_at) : null,
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, ConsumerMetadataItemEntity]>());

		return OrderedMap(items);
	}

	public generatePreviousExpositionConsumers(
		resp: IConsumerForDatablockExposition
	): { requestId: string; consumers: Array<ExpositionConsumerMappingEntity> } {
		let previouslyKnowConsumers = resp.users.reduce<
			Array<ExpositionConsumerMappingEntity>
		>((acc, curr) => {
			acc.push(this.adaptPreviousExpoUser(curr));
			return acc;
		}, []);

		previouslyKnowConsumers = resp.groups.reduce<
			Array<ExpositionConsumerMappingEntity>
		>((acc, curr) => {
			acc.push(this.adaptPreviousExpoGroup(curr));
			return acc;
		}, previouslyKnowConsumers);
		return {
			consumers: previouslyKnowConsumers,
			requestId: resp.request_id,
		};
	}

	public generateList(
		resp: ReadonlyArray<IExpositionResponse>,
		datablockId: number | null = null
	): ExpositionListEntity {
		const columns = this.generateExpositionList(datablockId === null);
		const columnsTuples = resp.reduce((acc, curr) => {
			if (datablockId) {
				if (
					curr.datablock &&
					curr.datablock.id &&
					curr.datablock.id === datablockId
				) {
					acc.push([`${curr.id}`, this.generateExpositionListItem(curr)]);
				}
			} else if (datablockId === null) {
				acc.push([`${curr.id}`, this.generateExpositionListItem(curr)]);
			}
			return acc;
		}, new Array<[string, ExpositionItemEntity]>());
		return columns.setElements(Map(columnsTuples));
	}

	public generateListAfterWS(
		expositionToUpdate: IExpositionResponse,
		listInStore: ExpositionListEntity
	): ExpositionListEntity {
		const item = this.generateExpositionListItem(expositionToUpdate);
		return listInStore.updateItem(item);
	}

	public generateReadyForMigrationExpositionList(
		resp: ReadonlyArray<IEligibleForMigration>
	): MigrationReadyExpositionList {
		const cols = MigrationReadyExpositionList.build([
			{
				field: 'label',
				alias: $localize`:i18n=@@exposition.migration.stepper.firstStep.grid.col1:`,
				type: 'string',
				visibleIndex: 0,
			},
			{
				field: 'accessPoint',
				alias: $localize`:i18n=@@exposition.migration.stepper.firstStep.grid.col2:`,
				type: 'string',
				visibleIndex: 1,
			},
		]);
		const columnsTuples = resp.reduce((acc, curr) => {
			acc.push([
				`${curr.id}`,
				MigrationReadyExpositionItemEntity.build({
					sourceDatablockId: curr.id,
					label: curr.label,
					accessPoint: curr.access_point,
					tags:
						curr?.tags?.map((t) =>
							AppTagItemEntity.build({
								id: t.id,
								label: t.code,
								color: t.color,
							})
						) ?? [],
				}),
			]);
			return acc;
		}, new Array<[string, MigrationReadyExpositionItemEntity]>());
		return cols.setElements(Map(columnsTuples));
	}

	public generateMigratedExpositionList(
		resp: ReadonlyArray<IMigratedExposition>
	): MigratedExpositionListEntity {
		const list = MigratedExpositionListEntity.build([
			{
				field: 'oldExpositionLabel',
				alias: $localize`:i18n=@@exposition.migrated.grid.col1:`,
				type: 'string',
				visibleIndex: 0,
			},
			{
				field: 'newExpositionLabel',
				alias: $localize`:i18n=@@exposition.migrated.grid.col2:`,
				type: 'string',
				visibleIndex: 1,
			},
			{
				field: 'migrationDate',
				alias: $localize`:i18n=@@exposition.migrated.grid.col3:`,
				type: 'date',
				visibleIndex: 2,
			},
		]);
		const tuples = resp.reduce((acc, curr, idx) => {
			const entity = MigratedExpositionItemEntity.build({
				id: idx,
				oldExpositionLabel: curr?.old_exposition_label ?? 'old',
				newExpositionLabel: curr?.new_exposition_label ?? 'new',
				newExpositionId: curr?.new_exposition_id ?? '0',
				oldExpositionId: curr?.old_exposition_id ?? '0',
				migrationDate: new Date(curr?.migration_date ?? ''),
			});
			acc.push([`${entity.newExpositionId}`, entity]);
			return acc;
		}, new Array<[string, MigratedExpositionItemEntity]>());
		return list.setElements(Map(tuples));
	}

	public generateExpositionHistoryList(
		response: Array<IPublicationHistoryDetails>
	): ExpositionHistoryListEntity {
		const list = ExpositionHistoryListEntity.build([
			{
				field: 'status',
				alias: $localize`:i18n=@@expositions.history.list.column.status:`,
				type: '',
				isSearchable: false,
				visibleIndex: 0,
				hasHeaderFilter: true,
			},
			{
				field: 'actionDescription',
				alias: $localize`:i18n=@@expositions.history.list.column.action:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 1,
				hasHeaderFilter: true,
			},
			{
				field: 'columns',
				alias: $localize`:i18n=@@expositions.history.list.column.config:`,
				type: '',
				isSearchable: false,
				visibleIndex: 2,
			},
			{
				field: 'dataEntries',
				alias: $localize`:i18n=@@expositions.history.list.column.lines:`,
				type: '',
				isSearchable: false,
				visibleIndex: 3,
			},
			{
				field: 'date',
				alias: $localize`:i18n=@@expositions.history.list.column.date:`,
				type: 'datetime',
				format: 'yyyy-MM-dd HH:mm:ss',
				isSearchable: true,
				visibleIndex: 4,
			},
			{
				field: 'duration',
				alias: $localize`:i18n=@@expositions.list.column.duration:`,
				type: '',
				isSearchable: true,
				visibleIndex: 5,
				hasHeaderFilter: false,
			},
			{
				field: 'avatar',
				alias: $localize`:i18n=@@expositions.history.list.column.triggeredBy:`,
				type: '',
				isSearchable: false,
				visibleIndex: 6,
				hasHeaderFilter: false,
			},
		]);

		const tuples = response.reduce((acc, curr) => {
			const entity = ExpositionHistoryItemEntity.build({
				id: curr.id ?? -1,
				access: curr.access_list ?? [],
				columns: curr.columns
					? curr.columns
							.filter((c) => c.active)
							.map((c) => ({
								label: c.label ?? '',
								type: c.type ? this.getColumnType(c.type) : ColumnType.Unknown,
								isList: c.list ?? false,
							}))
					: [],
				actionDescription: curr.action
					? this.getExpositionUpdateDescription(curr.action)
					: '',
				action: curr.action
					? this.getHistoryActionType(curr.action)
					: HistoryActionType.Unknown,
				duration: curr.duration ? curr.duration / 1000 : 0,
				avatar: curr.updated_by
					? AvatarEntity.build(
							`${curr.updated_by.first_name} ${curr.updated_by.last_name}`
					  )
					: AvatarEntity.build('user'),
				dataEntries: curr.nbr_of_lines,
				date: curr.date ? new Date(curr.date).toString() : '',
				status: curr.status
					? this.getPublicationStatus(curr.status)
					: PublicationStatus.Unknown,
				endPointMetadata: ExpositionEndpointMetadataEntity.build({
					accessPoint: curr.public_metadata?.access_point,
					title: curr.public_metadata?.title,
					details: curr.public_metadata?.details,
					keywords: OrderedSet(
						curr.public_metadata?.keywords?.map((keyword) =>
							AppTagItemEntity.buildKeyword({
								label: keyword,
							})
						) ?? []
					),
					synthesisType: curr.public_metadata?.is_synthesis_additive
						? 'Additive'
						: 'Restrictive',
				}),
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, ExpositionHistoryItemEntity]>());
		return list.setElements(
			OrderedMap(tuples.sort(([, e1], [, e2]) => e2.id - e1.id))
		);
	}

	public generateSimulationUsers(
		resp: ReadonlyArray<ExpositionSimulationUser>
	): Array<SimulationUserEntity> {
		return resp.map(
			(r) =>
				new SimulationUserEntity({
					id: r.id,
					email: r.mail,
					fullName: r.name,
				})
		);
	}

	public generateSimulationGroups(
		resp: ReadonlyArray<ExpositionSimulationGroup>
	): Array<SimulationGroupEntity> {
		return resp.map(
			(r) =>
				new SimulationGroupEntity({
					id: r.id,
					name: r.name,
				})
		);
	}

	public generateSimulationAccess(
		resp: ReadonlyArray<ExpositionSimulationAccess>
	): Array<SimulationAccessEntity> {
		return resp.map(
			(r) =>
				new SimulationAccessEntity({
					id: r.id,
					label: r.label,
					type: this.simulationAccessTypeAdapter[r.type],
				})
		);
	}

	public generateSimulationColumns(
		resp: ReadonlyArray<ExpositionsAvailableSimulationColumn>
	): SimulationColumnsListEntity {
		const list = SimulationColumnsListEntity.build([
			{
				field: 'type',
				alias: $localize`:i18n=@@general.column.type:`,
				type: 'string',
				visibleIndex: 0,
				isFilterable: false,
			},
			{
				field: 'exposedLabel',
				alias: $localize`:i18n=@@general.column.label:`,
				type: 'string',
				visibleIndex: 1,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@general.column.description:`,
				type: 'string',
				visibleIndex: 2,
			},
			{
				field: 'isPrimaryKey',
				alias: $localize`:i18n=@@general.column.primary-key:`,
				type: 'boolean',
				visibleIndex: 3,
			},
			{
				field: 'isFilterable',
				alias: $localize`:i18n=@@general.column.filterable:`,
				type: 'boolean',
				visibleIndex: 4,
			},
		]);
		const columns = this.generateAvailableSimulationColumnsItems(resp);
		return list.setElements(columns);
	}

	public generateErrorDescription(
		val: ParsedSimulationDataError
	): SimulationResultsEntity {
		return SimulationResultsEntity.buildWithErrors(val.code, val.msg);
	}

	public generateSimulationData(
		val: ParsedSimulationExec,
		availableSimCols: SimulationColumnsListEntity
	): SimulationResultsEntity {
		return SimulationResultsEntity.build('CHANGE_ME', {
			data: val.data,
			count: val.count,
			aliasToCol: availableSimCols,
		});
	}

	public generateSimulationResultWithRaw(
		val: Pick<ParsedSimulationExec, 'rawData'>,
		data: SimulationResultsEntity | null
	): SimulationResultsEntity {
		if (data === null) {
			return SimulationResultsEntity.empty();
		}
		return data.setRawData(val.rawData);
	}

	public generateAvailableUris(
		resp: SimulatedExpositionUris
	): SimulationUrisEntity {
		return SimulationUrisEntity.build({
			rest: {
				countUrl: resp.dcRestCountUrl,
				dataUrl: resp.dcRestDataUrl,
			},
			odata: {
				jsonUrl: resp.odataJsonUrl,
				xmlUrl: resp.odataXmlUrl,
			},
		});
	}

	public generateExpSimMetadata(
		resp: SimulationExpositionMetadata
	): SimulationExpositionMetadataEntity {
		return SimulationExpositionMetadataEntity.build({
			id: resp.id,
			urls: {
				rest: resp.data_root_url,
				odata: resp.odata_root_url,
				endpoint: resp.url,
			},
			keywords: resp.keywords,
			details: resp.details,
			linkedExpositionLabel: resp.metadata_label,
			endPointLabel: resp.title,
			publishedOn: dayjs(resp.publication_date),
			updatedOn: dayjs(resp.last_update_date),
		});
	}

	public generateAutoMappingConsumers(
		consumersMapping: IMembersMapping,
		consumersToMap: Map<string, ExpositionConsumerMappingEntity>
	): Array<ExpositionConsumerMappingEntity> {
		const autoMappingConsumers: Array<ExpositionConsumerMappingEntity> = [];

		const mappedUsers = consumersMapping.mapped_users
			? consumersMapping.mapped_users
			: [];
		const mappedGroups = consumersMapping.mapped_groups
			? consumersMapping.mapped_groups
			: [];

		mappedUsers.forEach((mappedUser) => {
			const sourceUser = mappedUser.source_member;
			const targetUser = mappedUser.target_member;
			if (!sourceUser) {
				return;
			}

			const consumerMapping = this.getConsumerMapping(
				consumersToMap,
				sourceUser,
				targetUser,
				false
			);

			if (!consumerMapping) {
				return;
			}

			autoMappingConsumers.push(consumerMapping);
		});

		mappedGroups.forEach((mappedGroup) => {
			const sourceGroup = mappedGroup.source_member;
			const targetGroup = mappedGroup.target_member;
			if (!sourceGroup) {
				return;
			}

			const consumerMapping = this.getConsumerMapping(
				consumersToMap,
				sourceGroup,
				targetGroup,
				true
			);

			if (!consumerMapping) {
				return;
			}

			autoMappingConsumers.push(consumerMapping);
		});

		return autoMappingConsumers;
	}

	private getConsumerMapping(
		consumersToMap: Map<string, ExpositionConsumerMappingEntity>,
		sourceMember: IConsumerToMap,
		targetMember: IMarketplaceMember,
		isGroup: boolean
	): ExpositionConsumerMappingEntity | null {
		const sourceEntity = consumersToMap.get(sourceMember.id);

		if (!sourceEntity) {
			return null;
		}

		const targets = [
			{
				id: targetMember.id,
				name: targetMember.name ?? '',
				isGroup,
				avatar:
					!isGroup && targetMember.name
						? AvatarEntity.build(targetMember.name)
						: null,
			},
		];

		return sourceEntity.updateTargetConsumers(Set(targets));
	}

	private generateAvailableSimulationColumnsItems(
		resp: ReadonlyArray<ExpositionsAvailableSimulationColumn>
	): OrderedMap<string, SimulationColumnsItemEntity> {
		return OrderedMap<string, SimulationColumnsItemEntity>(
			resp
				.map((it, idx): [string, SimulationColumnsItemEntity] => {
					const columnType = this.getColumnType(it.type);
					return [
						it.alias,
						SimulationColumnsItemEntity.build({
							id: idx,
							exposedLabel: it.alias,
							uuid: it.uuid,
							description: it.description,
							type: columnType,
							originalLabel: it.label,
							alias: it.alias,
							isList: it.list,
							position: it.position,
							isPrimaryKey: it.id,
							isFilterable: it.filter,
							dxDataType: this.columnTypeToDxColumnType[columnType],
						}),
					];
				})
				.sort((a, b) => a[1].position - b[1].position)
		);
	}

	private adaptPreviousExpoUser(
		pUser: IPreviousExpositionConsumer
	): ExpositionConsumerMappingEntity {
		if (pUser.id === undefined) {
			console.error('retrieved a user with no id, generating random uuid');
			return ExpositionConsumerMappingEntity.build({
				id: this.uuidService.uuidV4(),
				targetConsumers: Set(),
				fullName: `${pUser.first_name} ${pUser.last_name}`,
				unRecognized: true,
				description: pUser?.name ?? '',
				isGroup: false,
			});
		}
		return ExpositionConsumerMappingEntity.build({
			id: pUser.id,
			targetConsumers: Set(),
			fullName: `${pUser.first_name} ${pUser.last_name}`,
			unRecognized: !!(!pUser.enabled || pUser.unknown),
			description: pUser?.name ?? '',
			isGroup: false,
		});
	}

	private adaptPreviousExpoGroup(
		pGroup: IPreviousExpositionConsumer
	): ExpositionConsumerMappingEntity {
		if (pGroup.id === undefined) {
			console.error('retrieved a group with no id, generating random uuid');
			return ExpositionConsumerMappingEntity.build({
				id: this.uuidService.uuidV4(),
				targetConsumers: Set(),
				fullName: `${pGroup.label}`,
				unRecognized: true,
				description: pGroup?.description ?? '',
				isGroup: true,
			});
		}
		return ExpositionConsumerMappingEntity.build({
			id: pGroup.id,
			targetConsumers: Set(),
			fullName: `${pGroup.label}`,
			unRecognized: !!(!pGroup.enabled || pGroup.unknown),
			description: pGroup?.description ?? '',
			isGroup: true,
		});
	}

	private generateExpositionListItem(
		resp: IExpositionResponse
	): ExpositionItemEntity {
		const tags =
			resp.metadata?.tags?.map((t) =>
				AppTagItemEntity.build({
					id: t.id,
					label: t.code,
					color: t.color,
				})
			) ?? [];

		const expositionActionBy = resp.draft_publication?.drafted_by
			? AvatarEntity.build(
					`${resp.draft_publication?.drafted_by?.first_name} ${resp.draft_publication?.drafted_by?.last_name}`,
					2
			  )
			: null;
		const elemModifiedBy = AvatarEntity.build(
			`${resp.last_updated_by?.first_name} ${resp.last_updated_by?.last_name}`,
			2
		);
		const apiStatus = resp.status
			? this.getPublicationStatus(resp.status, resp.history)
			: PublicationStatus.Unknown;
		return ExpositionItemEntity.build({
			id: resp.id,
			label: `${resp.metadata?.label}`,
			expositionUrl: resp?.url ?? '',
			sourceUrl: resp.datablock?.id
				? this.parseDatablockPageUrl(resp.datablock.id)
				: '',
			tags: OrderedSet(tags),
			datablockSource: resp?.datablock?.label ?? '',
			apiStatus,
			columnsCount: resp.nbr_of_cols ?? 0,
			dataEntries: resp?.count ?? 0,
			accessCount: resp.access ?? 0,
			duration: resp?.duration_ms ? `${resp?.duration_ms / 1000}` : '0',
			accessPointLabel: resp?.draft_publication?.access_point ?? '',
			accessPointUrl: resp?.url ?? '',
			lastExpositionActionBy: expositionActionBy,
			lastExpositionAction: resp?.draft_publication?.update_date ?? null,
			lastElemModificationBy: elemModifiedBy,
			lastElemModification: resp.metadata?.update_date
				? new Date(resp.metadata.update_date)
				: null,
			actionInProgress:
				apiStatus === PublicationStatus.Running ? 'in-progress' : 'none',
		});
	}

	private generateExpositionList(withSource: boolean): ExpositionListEntity {
		let availableCols: Array<IExpositionListExtra> = [
			{
				field: 'label',
				alias: $localize`:i18n=@@expositions.list.column.label:`,
				type: 'string',
				isSearchable: true,
				hasHeaderFilter: false,
				visibleIndex: 0,
			},
			{
				field: 'tags',
				alias: 'Tags',
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 1,
			},
			{
				field: 'lastElemModification',
				alias: $localize`:i18n=@@expositions.list.column.lastMod:`,
				type: 'datetime',
				format: 'dd-MM-yyyy HH:mm',
				hasHeaderFilter: false,
				isSearchable: true,
				visibleIndex: 2,
			},
			{
				field: 'lastElemModificationBy',
				alias: $localize`:i18n=@@expositions.list.column.lastModBy:`,
				type: 'string',
				isSearchable: false,
				hasHeaderFilter: true,
				isFilterable: true,
				visibleIndex: 3,
			},
			{
				field: 'apiStatus',
				alias: $localize`:i18n=@@expositions.list.column.APIStatus:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				isFilterable: false,
				visibleIndex: 4,
			},
			{
				field: 'accessPointLabel',
				alias: $localize`:i18n=@@expositions.list.column.accessPoint:`,
				type: '',
				isSearchable: true,
				hasHeaderFilter: false,
				visibleIndex: 5,
			},
			{
				field: 'columnsCount',
				alias: $localize`:i18n=@@expositions.list.column.configuration:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: false,
				visibleIndex: 6,
			},
			{
				field: 'accessCount',
				alias: 'accessCount',
				type: '',
				isSearchable: false,
				hasHeaderFilter: false,
				visibleIndex: 7,
			},
			{
				field: 'dataEntries',
				alias: $localize`:i18n=@@expositions.list.column.lines:`,
				type: 'number',
				isSearchable: true,
				hasHeaderFilter: false,
				visibleIndex: 8,
			},
			{
				field: 'duration',
				alias: $localize`:i18n=@@expositions.list.column.duration:`,
				type: 'number',
				isSearchable: true,
				hasHeaderFilter: false,
				visibleIndex: 9,
			},
			{
				field: 'lastExpositionAction',
				alias: $localize`:i18n=@@expositions.list.column.lastPublicationUpdate:`,
				isSearchable: true,
				type: 'datetime',
				format: 'dd-MM-yyyy HH:mm',
				hasHeaderFilter: false,
				visibleIndex: 10,
			},
			{
				field: 'lastExpositionActionBy',
				alias: $localize`:i18n=@@expositions.list.column.lastPublicationUpdateBy:`,
				isSearchable: false,
				type: 'string',
				hasHeaderFilter: true,
				visibleIndex: 11,
				isFilterable: true,
			},
		];
		if (withSource) {
			availableCols.push({
				field: 'datablockSource',
				alias: $localize`:i18n=@@expositions.list.column.source:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				visibleIndex: 1,
			});
		}
		availableCols = this.dxListsStorage.synchronize<
			IExpositionListExtra,
			ExpositionItemEntity
		>(DcDxGrids.ExpositionListStandalone, availableCols);
		return ExpositionListEntity.build(availableCols);
	}

	private generateColumnConfigList(): ExpositionColumnConfigListEntity {
		return ExpositionColumnConfigListEntity.build([
			{
				field: 'columnType',
				alias: '',
				type: '',
				isSearchable: false,
				visibleIndex: 0,
			},
			{
				field: 'label',
				alias: $localize`:@@exposition.columns.config.column.label:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 1,
			},
			{
				field: 'alias',
				alias: $localize`:@@exposition.columns.config.column.alias:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 2,
			},
			{
				field: 'isActive',
				alias: $localize`:@@exposition.columns.config.column.active:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 3,
			},
			{
				field: 'description',
				alias: $localize`:@@exposition.columns.config.column.description:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 4,
			},

			{
				field: 'isPrimaryKey',
				alias: $localize`:@@exposition.columns.config.column.id:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 5,
			},
			{
				field: 'isFiltered',
				alias: $localize`:@@exposition.columns.config.column.filterable:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 6,
			},
			{
				field: 'isCaseSensitive',
				alias: $localize`:@@exposition.columns.config.column.caseSensitive:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 7,
			},
			{
				field: 'isHashed',
				alias: $localize`:@@exposition.columns.config.column.hashable:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 8,
			},
			{
				field: 'isHidden',
				alias: $localize`:@@exposition.columns.config.column.hide:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 9,
			},
			{
				field: 'position',
				alias: $localize`:@@exposition.columns.config.column.position:`,
				type: 'integer',
				isSearchable: true,
				visibleIndex: 10,
			},
		]);
	}

	private generateExpositionDatablockList(): ExpositionDatablockListEntity {
		return ExpositionDatablockListEntity.build([
			{
				field: 'label',
				alias: $localize`:i18n=@@exposition.datablock.list.column.label:`,
				type: 'string',
				isSearchable: true,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'columnsCount',
				alias: $localize`:i18n=@@exposition.datablock.list.column.columns:`,
				type: 'number',
				isSearchable: false,
				visibleIndex: 1,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@exposition.datablock.list.column.lastUpdate:`,
				type: 'datetime',
				format: 'dd-MM-yyyy HH:mm',
				hasHeaderFilter: true,
				isSearchable: true,
				isFilterable: true,
				visibleIndex: 2,
			},
			{
				field: 'avatar',
				alias: '',
				type: '',
				hasHeaderFilter: false,
				isSearchable: false,
				isFilterable: false,
				visibleIndex: 3,
			},
		]);
	}

	private generateExpositionDatablockEntity(
		response: IShareableDatablock
	): ExpositionDatablockEntity {
		return ExpositionDatablockEntity.build({
			id: response.id,
			label: response.label,
			updateDate: new Date(response.last_update ?? ''),
			url: response.id ? this.parseDatablockPageUrl(response.id) : '',
			tags: response.tags?.map((t) =>
				AppTagItemEntity.build({
					id: t.id,
					label: t.code,
					color: t.color,
				})
			),
			columnsCount: response.column_count,
			avatar: AvatarEntity.build(
				`${response.last_update_by?.first_name} ${response.last_update_by?.last_name}`
			),
		});
	}

	private generateDatablockColumnEntity(
		response: IShareableDatablockColumn
	): ExpositionDatablockColumnEntity {
		return ExpositionDatablockColumnEntity.build({
			label: response.lib,
			type: response.type
				? this.getColumnType(response.type)
				: ColumnType.Unknown,
			isList: response.is_list,
		});
	}

	private generateColumnConfigEntity(
		response: IShareableDatablockColumn
	): ExpositionColumnConfigEntity {
		return ExpositionColumnConfigEntity.build({
			id: response.uuid,
			label: response.lib,
			columnType: response.type
				? this.getColumnType(response.type)
				: ColumnType.Unknown,
			alias: response.lib ? normalize(response.lib) : '',
			isList: response.is_list,
		});
	}

	private generateExpositionConsumerList(): ExpositionConsumerListEntity {
		return ExpositionConsumerListEntity.build([
			{
				field: 'avatar',
				alias: '',
				type: '',
				isSearchable: false,
				visibleIndex: 0,
			},
			{
				field: 'name',
				alias: $localize`:@@expositions.access.config.userOrGroupName:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 1,
			},
		]);
	}

	private parseDatablockPageUrl(datablockId: number): string {
		const projectId = this.projectStorage.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			datablockId,
			'DATABLOCK'
		);
	}

	private getColumnType(type: string): ColumnType {
		switch (type.toLowerCase()) {
			case 'string':
				return ColumnType.Str;
			case 'file':
				return ColumnType.File;
			case 'double':
				return ColumnType.Double;
			case 'decimal':
				return ColumnType.Dec;
			case 'date':
				return ColumnType.Date;
			case 'big_integer':
				return ColumnType.BigInt;
			case 'binary':
				return ColumnType.Bin;
			case 'words':
				return ColumnType.Words;
			case 'integer':
				return ColumnType.Int;
			case 'boolean':
				return ColumnType.Bool;
			case 'geometry':
				return ColumnType.Geo;
			default:
				return ColumnType.Unknown;
		}
	}

	private getPublicationStatus(
		status: HistoryStatus,
		history?: Array<IPublicationHistory>
	): PublicationStatus {
		if (
			status === HistoryStatus.UNPUBLISHED &&
			history &&
			history.length === 0
		) {
			return this.PublicationStatusMapping[status];
		} else if (
			status === HistoryStatus.UNPUBLISHED &&
			history &&
			history.length !== 0
		) {
			const latestKnownStatus = history[0].status ?? HistoryStatus.UNKNOWN;
			return this.PublicationStatusMapping[latestKnownStatus];
		} else if (
			status === HistoryStatus.AVAILABLE &&
			history &&
			history.length !== 0
		) {
			const latestKnownStatus =
				history[0].status !== HistoryStatus.RUNNING
					? status
					: history[0].status;
			return this.PublicationStatusMapping[latestKnownStatus];
		}
		return this.PublicationStatusMapping[status];
	}

	private getExpositionUpdateDescription(update: ActionType): string {
		switch (update) {
			case ActionType.UPDATE_PUBLIC_METADATA:
				return $localize`:i18n=@@expositions.publication.api.updateType.configuration:`;
			case ActionType.UPDATE_ACCESS:
				return $localize`:i18n=@@expositions.publication.api.updateType.configuration:`;
			case ActionType.UPDATE_COLUMNS:
				return $localize`:i18n=@@expositions.publication.api.updateType.configuration:`;
			case ActionType.UPDATE_DATA:
				return $localize`:i18n=@@expositions.publication.api.updateType.data:`;
			case ActionType.UPDATE_CONF:
				return $localize`:i18n=@@expositions.publication.api.updateType.configuration:`;
			case ActionType.PUBLISH_DATA:
				return $localize`:i18n=@@expositions.publication.api.updateType.publish:`;
			case ActionType.CLEAR_DATA:
				return $localize`:i18n=@@expositions.publication.api.updateType.clear:`;
			case ActionType.ENABLE:
				return $localize`:i18n=@@expositions.publication.api.updateType.activate:`;
			case ActionType.DISABLE:
				return $localize`:i18n=@@expositions.publication.api.updateType.deactivate:`;
			default:
				return '';
		}
	}

	private getHistoryActionType(update: ActionType): HistoryActionType {
		switch (update) {
			case ActionType.UPDATE_PUBLIC_METADATA:
				return HistoryActionType.UpdateConfig;
			case ActionType.UPDATE_ACCESS:
				return HistoryActionType.UpdateConfig;
			case ActionType.UPDATE_COLUMNS:
				return HistoryActionType.UpdateConfig;
			case ActionType.UPDATE_DATA:
				return HistoryActionType.UpdateData;
			case ActionType.UPDATE_CONF:
				return HistoryActionType.UpdateConfig;
			case ActionType.PUBLISH_DATA:
				return HistoryActionType.Publish;
			case ActionType.CLEAR_DATA:
				return HistoryActionType.ClearData;
			case ActionType.ENABLE:
				return HistoryActionType.Activate;
			case ActionType.DISABLE:
				return HistoryActionType.Deactivate;
			default:
				return HistoryActionType.Unknown;
		}
	}

	private generateColumnEntity(
		response: IExpositionColumnConfig
	): ExpositionColumnConfigEntity {
		return ExpositionColumnConfigEntity.build({
			id: response.uuid,
			label: response.label,
			description: response.description,
			columnType: response.type
				? this.getColumnType(response.type)
				: ColumnType.Unknown,
			alias: response.alias ?? normalize(response.label ?? ''),
			isList: response.list,
			isActive: response.active,
			isHashed: response.hash,
			isFiltered: response.filter,
			isCaseSensitive: response.case_insensitive,
			isPrimaryKey: response.id,
			isHidden: response.hidden,
			position: response.position,
		});
	}

	private generateAccessEntity(
		response: IExpositionPublicationAccessConfig
	): ExpositionAccessEntity {
		return ExpositionAccessEntity.build({
			id: response.id,
			label: response.label,
			isActive: response.active,
			accessType:
				response.type === 'LIMITED' ? AccessType.Limited : AccessType.Open,
			usersCount: response.members?.users.length,
			groupsCount: response.members?.groups.length,
			columnsCount: response.columns?.filter((c) => c.active).length,
			columns: response.columns
				? response.columns
						.filter((c) => c.active)
						.sort((a, b) => {
							const posA = a.position ?? -1;
							const posB = b.position ?? -1;

							if (posA === -1 && posB === -1) {
								return 0;
							}
							if (posA === -1) {
								return 1;
							}
							if (posB === -1) {
								return -1;
							}

							return posA - posB;
						})
						.map((c) => ({
							label: c.label ?? '',
							type: c.type ? this.getColumnType(c.type) : ColumnType.Unknown,
						}))
				: [],
			users: response.members?.users.map((user) => ({
				label: `${user.name}`,
				avatar: AvatarEntity.build(`${user.name}`, 2),
			})),
			groups: response.members?.groups.map((group) => ({
				label: `${group.name}`,
				avatar: AvatarEntity.build(`${group.name}`, 1),
			})),
			lines: response.filter,
		});
	}

	private generateAccessList(): ExpositionAccessListEntity {
		return ExpositionAccessListEntity.build([
			{
				field: 'label',
				alias: $localize`:i18n=@@exposition.access.list.column.label:`,
				type: 'string',
				isSearchable: true,
				visibleIndex: 0,
			},
			{
				field: 'accessType',
				alias: $localize`:i18n=@@exposition.access.list.column.accessType:`,
				type: '',
				isSearchable: false,
				visibleIndex: 1,
			},
			{
				field: 'users',
				alias: $localize`:i18n=@@exposition.access.list.column.consumers:`,
				type: '',
				isSearchable: false,
				visibleIndex: 2,
			},
			{
				field: 'columns',
				alias: $localize`:i18n=@@exposition.access.list.column.columns:`,
				type: 'number',
				isSearchable: false,
				visibleIndex: 3,
			},
			{
				field: 'lines',
				alias: $localize`:i18n=@@exposition.access.list.column.lines:`,
				type: '',
				isSearchable: false,
				visibleIndex: 4,
			},
		]);
	}
}
